// How a lane's cells become marks and spans at a given zoom.
//
// This is the geometry the whole lanes plane agrees on: the marks themselves,
// the influence overlay's endpoints, the join pins, the inset connectors and the
// turn range a focused mark stands for all resolve positions through `markX` and
// `markSpan`, so a drawn annotation lands on the mark it belongs to at every
// zoom. It lives here rather than inside the view because it is a pure function
// of (cells, cellW) with several rules that are worth asserting directly — the
// bucket merge, the run fusion, where a run's segments split, and which kind a
// merged run takes.
import { bucketMarks, cellKind } from "./traceLanes.js";

// Where per-turn marks stop being drawable and bucketing takes over.
export const MARK_MIN_PX = 7;

// The mark palette, one entry per cellKind. Lives with the mark geometry so
// every surface that draws lane marks — the lanes view and the anatomy hero's
// zoomed-out reading — says "changed"/"errored"/"consulted" in the same color.
export const MARK_COLOR = {
  read: "var(--muted)",
  write: "var(--green)",
  error: "var(--red,#c0392b)",
  prompt: "var(--eyebrow)",
  reply: "var(--muted)",
  // Mixed down rather than taken from a token: --muted-soft and --eyebrow sit
  // within 0.06 alpha of each other, so every "quieter" token in the palette is
  // in fact brighter than the prompt beat. A note must read below the beats.
  note: "color-mix(in srgb, var(--muted) 55%, transparent)",
  // Model-verified readings: the reading green, drawn DASHED (see the lanes
  // view's Mark) so a synthesized-on-the-fly mark never passes for an
  // extractor claim.
  verified: "var(--ok, #7fc9a3)",
  // A real residual call the reading harvested: solid border (the call is
  // quoted-tier real), reading tone (the model read it).
  readCall: "var(--ok, #7fc9a3)",
};

// How many turns one mark covers at this zoom: one, until a turn column gets
// narrower than a mark and adjacent activity has to merge.
export const markSpan = (cellW) => (cellW >= MARK_MIN_PX ? 1 : Math.ceil(MARK_MIN_PX / cellW));
export const markX = (turn, cellW) => {
  const span = markSpan(cellW);
  return (Math.floor(turn / span) * span + span / 2) * cellW;
};
// The turns a mark drawn for `turn` stands for, both ends inclusive.
export const markTurnRange = (turn, cellW) => {
  const span = markSpan(cellW);
  const start = Math.floor(turn / span) * span;
  return { fromTurn: start, toTurn: start + span - 1 };
};

// Severity order for the majority tiebreak in laneMarks.
const SEVERITY = { error: 0, write: 1, read: 2, prompt: 3, reply: 4, note: 5 };

// One lane's marks at the current zoom: per-turn when a turn column can hold a
// mark, merged buckets when it can't. Runs of ADJACENT occupied buckets fuse
// into one continuous span: dense activity reads as a bar instead of a stream
// of side-by-side glyphs, and the per-bucket marks under it become invisible
// hover/click zones, so pointing anywhere along the bar still reads exactly
// the ops at that position. An empty bucket breaks the run - a gap in the bar
// is a stretch of turns where nothing touched the subject, and bridging it
// would claim otherwise.
export function laneMarks(cells, cellW) {
  const span = markSpan(cellW);
  const segW = span * cellW;
  const marks = bucketMarks(cells, span).map((b) => ({
    first: b.first,
    last: b.last,
    kind: cellKind(b.items),
    count: b.items.length,
    start: b.start,
    x: b.start * cellW + segW / 2,
  }));
  const spans = [];
  let i = 0;
  while (i < marks.length) {
    let j = i;
    while (j + 1 < marks.length && marks[j + 1].start === marks[j].start + span) j += 1;
    if (j > i) {
      // Segments split only where the KIND changes, so a long same-kind
      // stretch is one rect: per-bucket rects at fractional widths leave
      // subpixel seams, and with alpha fills every seam draws as a stripe.
      const segs = [];
      let count = 0;
      const kindBuckets = new Map();
      for (let k = i; k <= j; k++) {
        marks[k].merged = true;
        count += marks[k].count;
        kindBuckets.set(marks[k].kind, (kindBuckets.get(marks[k].kind) || 0) + 1);
        const x1 = marks[k].start * cellW + segW;
        const prev = segs[segs.length - 1];
        if (prev && prev.kind === marks[k].kind) prev.x1 = x1;
        else segs.push({ x0: marks[k].start * cellW, x1, kind: marks[k].kind, first: marks[k].first });
      }
      // The container's kind is the run's MAJORITY bucket kind (severity
      // breaks ties): one failed call inside a long write burst must not
      // paint the whole run red - the error keeps its red segment, sited
      // where it happened, and an all-error run still reads red overall.
      const kind = [...kindBuckets.entries()].sort(
        (a, b) => b[1] - a[1] || (SEVERITY[a[0]] ?? 9) - (SEVERITY[b[0]] ?? 9),
      )[0][0];
      spans.push({ x0: marks[i].start * cellW, x1: marks[j].start * cellW + segW, segs, count, kind });
    }
    i = j + 1;
  }
  return { marks, spans };
}

// Fill for a kind-run inside a merged span: the count badge's own tint
// recipe, a notch stronger so a write or error stretch reads against the
// consulted (transparent) stretches beside it.
export const SPAN_SEG_MIX = { read: 0, write: 30, error: 40, prompt: 22, reply: 16, note: 12 };
